import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';
import styledC, { keyframes } from 'styled-components';

import { BasicButton } from '@adsk/alloy-react-button';
import { variantSwitch, useDeprecate } from '@adsk/alloy-react-helpers';
import { XIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { NOTIFICATION_STATUSES, NotificationStatus } from './helpers/consts';
import NotificationIcon from './NotificationIcon';

const fadeIn = keyframes`
  from {
    top: -16px;
    opacity: 0;
  }

  to {
    top: 0;
    opacity: 1;
  }
`;

type AsyncResult = {
  status?: NotificationStatus;
  message?: string;
  footer?: ReactNode;
};
type NotificationProps = StylableComponent<HTMLDivElement> & {
  footer?: ReactNode;
  onDismiss?: () => void;
  /**
   * @deprecated
   * autoDismiss should be applied to useNotifications().showNotification
   */
  autoDismiss?: number;
  dismissible?: boolean;
  showIcon?: boolean;
  status?: NotificationStatus;
  asyncOperation?: Promise<unknown>;
};

const StyledDiv = styledC.div`
  min-width: 320px;
  max-width: 416px;
  min-height: 48px;
  background-color: ${theme.tokens.colors.background.primary.value};
  display: flex;
  flex-direction: column;
  border-radius: ${theme.borderRadius.medium}px;
  border-left: ${theme.tokens.colors.border.information.value} solid 4px;
  box-shadow: ${theme.shadows.lowElevation};
  padding: ${theme.spacing.S}px ${theme.spacing.M}px;
  box-sizing: border-box;
  animation: ${fadeIn} 0.2s ease-out;
`;

const StyledContent = styledC.div`
  display: flex;
`;

const StyledMessage = styledC.div`
  flex: 1;
  padding: ${theme.spacing.XXXS}px 0;
  word-break: break-word;
`;

const StyledBasicButton = styledC(BasicButton)`
  height: 24px;
  color: ${theme.tokens.colors.icon.secondary.value};
  margin-left: 26px;
`;

const StyleNotificationIcon = styledC(NotificationIcon)`
  margin-right: 10px;
`;

const Notification = React.forwardRef<HTMLDivElement, NotificationProps>(
  (
    {
      style,
      className,
      status = NOTIFICATION_STATUSES.INFO,
      showIcon = true,
      children,
      footer,
      dismissible = true,
      onDismiss,
      asyncOperation,
      autoDismiss,
      ...props
    },
    ref,
  ) => {
    useDeprecate(
      autoDismiss !== undefined,
      'Notification',
      'autoDismiss',
      'useNotifications().showNotification autoDismiss argument',
    );

    const hasPromise =
      asyncOperation && typeof asyncOperation.then === 'function';
    const [asyncResult, setAsyncResult] = React.useState<AsyncResult>();
    React.useEffect(() => {
      if (hasPromise) {
        asyncOperation
          ?.then((result) => setAsyncResult(result as AsyncResult))
          .catch((result) => setAsyncResult(result));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const isLoading = hasPromise && !asyncResult;
    const finalStatus: NotificationStatus | undefined =
      hasPromise && asyncResult && asyncResult.status
        ? asyncResult.status
        : status;
    const asyncDismissible = hasPromise && asyncResult;
    const finalShowIcon = showIcon && status !== NOTIFICATION_STATUSES.NONE;

    return (
      <StyledDiv
        {...props}
        className={className}
        ref={ref}
        role="alert"
        css={[
          variantSwitch({
            statement: finalStatus,
            variants: {
              [NOTIFICATION_STATUSES.NONE]: {
                borderColor: 'transparent',
              },
              [NOTIFICATION_STATUSES.INFO]: {
                borderColor: theme.tokens.colors.border.information.value,
                backgroundColor:
                  theme.tokens.colors.background.information.subtler.value,
              },
              [NOTIFICATION_STATUSES.SUCCESS]: {
                borderColor: theme.tokens.colors.border.success.value,
                backgroundColor:
                  theme.tokens.colors.background.success.subtler.value,
              },
              [NOTIFICATION_STATUSES.WARNING]: {
                borderColor: theme.tokens.colors.border.warning.value,
                backgroundColor:
                  theme.tokens.colors.background.warning.subtler.value,
              },
              [NOTIFICATION_STATUSES.ERROR]: {
                borderColor: theme.tokens.colors.border.error.value,
                backgroundColor:
                  theme.tokens.colors.background.error.subtler.value,
              },
            },
          }),
          style,
        ]}
      >
        <StyledContent>
          {finalShowIcon && !isLoading && (
            <StyleNotificationIcon status={finalStatus} />
          )}
          {isLoading && (
            <ProgressRing
              style={{ marginRight: theme.spacing.M }}
              size={ProgressRing.SIZES.SMALL}
            />
          )}
          <StyledMessage>
            {asyncResult && asyncResult.message
              ? asyncResult.message
              : children}
          </StyledMessage>
          {(dismissible !== false || asyncDismissible) && (
            <StyledBasicButton aria-label="close" onClick={onDismiss}>
              <XIcon />
            </StyledBasicButton>
          )}
        </StyledContent>
        {asyncResult && 'footer' in asyncResult ? asyncResult.footer : footer}
      </StyledDiv>
    );
  },
);

Notification.displayName = 'Notifications';

Notification.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Notification's content */
  children: PropTypes.any,
  /** Notification's footer */
  footer: PropTypes.any,
  /** Called on dismiss */
  onDismiss: PropTypes.func,
  /** Deprecated: Controls whether the notification should automatically dismiss */
  autoDismiss: PropTypes.number,
  /** Controls whether the notification is dismissible */
  dismissible: PropTypes.bool,
  /** Toggle icon visibility */
  showIcon: PropTypes.bool,
  /** Notification status */
  status: PropTypes.oneOf(Object.values(NOTIFICATION_STATUSES)),
  /** A Promise if the toast needs to be changed after rendered.
   When the promise resolves it should return an object with `message` and `status` to update the toast state. */
  asyncOperation: PropTypes.instanceOf(Promise),
};

export default Notification;
